import React, { useEffect, useState } from 'react';

const PROMO_SHOWN_KEY      = 'ff:promo:shown';
const PROMO_GEN_COUNT_KEY  = 'ff:promo:genCount';
const PROMO_TRIGGER_AT     = 2;  // show after this many generations
const DISCOUNT_CODE        = 'FLASH15';

/**
 * Call this every time a flashcard is generated for an anonymous/free user.
 * Returns true if the promotional modal should now be shown.
 */
export function trackGenerationForPromo() {
    try {
        const shown = localStorage.getItem(PROMO_SHOWN_KEY) === '1';
        if (shown) return false;
        const count = Number(localStorage.getItem(PROMO_GEN_COUNT_KEY) || 0) + 1;
        localStorage.setItem(PROMO_GEN_COUNT_KEY, String(count));
        if (count >= PROMO_TRIGGER_AT) {
            return true;
        }
    } catch (_) {}
    return false;
}

export function markPromoShown() {
    try { localStorage.setItem(PROMO_SHOWN_KEY, '1'); } catch (_) {}
}

export function resetPromoForTesting() {
    try {
        localStorage.removeItem(PROMO_SHOWN_KEY);
        localStorage.removeItem(PROMO_GEN_COUNT_KEY);
    } catch (_) {}
}

/**
 * "Enjoying FlashFonic?" promotional funnel modal.
 * Props:
 *   onClose           - called when dismissed
 *   onGoToPricing     - called when user clicks the pricing CTA
 *   onSignUp          - called when user clicks sign up directly
 *   variant           - 'enjoying' | 'limit_hit' | 'trial_expired'
 */
const PromotionalModal = ({ onClose, onGoToPricing, onSignUp, variant = 'enjoying' }) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => { markPromoShown(); }, []);

    const copyCode = () => {
        try { navigator.clipboard.writeText(DISCOUNT_CODE); } catch (_) {}
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const configs = {
        enjoying: {
            badge: '🎉 Special Offer',
            badgeColor: 'text-sky-300 bg-sky-500/10 border-sky-400/20',
            headline: 'Enjoying FlashFonic?',
            sub: "You're on a roll! Sign up today and get 15% off your first month — plus 100 bonus flashcard credits on us.",
            ctaLabel: 'Claim 15% Off →',
            ctaColor: 'from-sky-500 to-blue-500',
            showCode: true,
        },
        limit_hit: {
            badge: '⚡ Upgrade to Continue',
            badgeColor: 'text-amber-300 bg-amber-500/10 border-amber-400/20',
            headline: "You've hit the free limit",
            sub: "You've used all your free generations. Upgrade now and get 15% off your first month — no commitment required.",
            ctaLabel: 'See Plans & Unlock →',
            ctaColor: 'from-amber-500 to-orange-500',
            showCode: true,
        },
        trial_expired: {
            badge: '🏁 Free Trial Complete',
            badgeColor: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20',
            headline: "You've used all 10 free board questions",
            sub: 'Ready to keep going? Sign up to unlock unlimited clinical board questions, flashcards, and your full study suite.',
            ctaLabel: 'Start Free — Then Upgrade →',
            ctaColor: 'from-emerald-500 to-teal-500',
            showCode: false,
        },
    };

    const cfg = configs[variant] || configs.enjoying;

    return (
        <div className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center p-0 sm:p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            <div
                className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0d1628] shadow-2xl overflow-hidden"
                style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif' }}
            >
                {/* Glow */}
                <div aria-hidden className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />

                <div className="relative p-6 sm:p-8">
                    {/* Close */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-full bg-white/5 text-slate-400 hover:text-white transition"
                        aria-label="Close"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Badge */}
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium mb-4 ${cfg.badgeColor}`}>
                        {cfg.badge}
                    </span>

                    <h2 className="text-xl font-semibold text-white tracking-tight">{cfg.headline}</h2>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{cfg.sub}</p>

                    {/* Promo code */}
                    {cfg.showCode && (
                        <div className="mt-5 flex items-center gap-2">
                            <div className="flex-1 rounded-xl border border-dashed border-amber-400/40 bg-amber-500/5 px-4 py-2.5">
                                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-medium">Promo code</p>
                                <p className="text-lg font-semibold text-amber-300 tracking-widest">{DISCOUNT_CODE}</p>
                            </div>
                            <button
                                onClick={copyCode}
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-slate-800/60 text-slate-300 hover:text-white hover:border-white/20 transition"
                                title="Copy code"
                            >
                                {copied ? (
                                    <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Feature bullets */}
                    <ul className="mt-5 space-y-2">
                        {[
                            'Unlimited flashcard generation',
                            'FlashFonic Clinicals board questions',
                            'Smart Notes & PDF export',
                            'Games, streaks & spaced repetition',
                        ].map(f => (
                            <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                                <svg className="h-3.5 w-3.5 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {f}
                            </li>
                        ))}
                    </ul>

                    {/* CTAs */}
                    <div className="mt-6 space-y-2">
                        <button
                            onClick={() => { onGoToPricing?.(); onClose?.(); }}
                            className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r ${cfg.ctaColor} px-6 py-3 text-sm font-medium text-white shadow-lg hover:opacity-95 transition`}
                        >
                            {cfg.ctaLabel}
                        </button>
                        {onSignUp && (
                            <button
                                onClick={() => { onSignUp?.(); onClose?.(); }}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-2.5 text-sm text-slate-300 hover:text-white hover:border-white/20 transition"
                            >
                                Sign up first (it's free)
                            </button>
                        )}
                    </div>

                    <p className="mt-3 text-center text-xs text-slate-600">No credit card required. Cancel anytime.</p>
                </div>
            </div>
        </div>
    );
};

export default PromotionalModal;
